// The draft room (DRAFT_ROOM.md, PLAN-2026-10-14): one screen for the
// practice league's room, a real league's and a mock's. It draws a
// `DraftView` and sends intents through `actions`; whether those go to the
// simulator or over the room's socket is the caller's business
// (`DraftRoomScreen.tsx`).
//
// BUILT TO FILL THE SCREEN (UX-38's exception): the clock is always in view,
// and each region scrolls on its own. A phone shows one region at a time
// under the clock; a tablet the board over the players and one more pane; a
// desktop the board over the players, the research and mine; a large monitor
// all of it side by side (DIRECTION U7, U21).

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWince } from './wince';
import {
  LEAGUE_MOCK_PAUSE_MINUTES,
  dailyStopEnds,
  formatInZone,
  type DraftView,
} from '@fsp/domain';
import type { OfferNews } from '../../data/liveDraft';
import { OPEN_SEAT } from '@fsp/draft-room';
import { useDetail } from '../../data/detail';
import { deviceZone, useReaderZone } from '../../data/hooks';
import { useHoldMoments } from '../../data/achievements';
import { useDelight } from '../../delight/useDelight';
import { TeamMark } from '../../kit';
import { Menu, type MenuEntry } from '../../kit/overlays';
import { Button, Callout, Caption, IconButton, Segments, Toggle, tell } from '../../kit/ui';
import { ChatPanel } from '../../shell/ChatPanel';
import { useTier } from '../../shell/useTier';
import { getSport } from '../../sports/registry';
import '../../styles/screens/draft-room.css';
import {
  BoardPane,
  MyTeamPane,
  PicksPane,
  PlayersPane,
  QueuePane,
  SeatsPane,
  TeamsPane,
  TiersPane,
} from './panes';
import { pickLabel, picksUntilMine } from './research';
import { DraftCards, type DraftCardActions } from './DraftCards';
import { AuctionLots, MoneyPane, auctionBarText, type AuctionActions } from './AuctionBlock';
import {
  MyOffers,
  OfferToMe,
  TradeSheet,
  TradesMade,
  type TradeActions,
  type TradeSeed,
} from './DraftTrades';

export type RoomConnection =
  | 'practice'
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'offline'
  | 'signed-out'
  | 'removed'
  | 'closed';

export interface RoomActions {
  /** Draft a player: for my own team, or, as commissioner, for the team on the clock. */
  pick: (playerId: string, forTeam: string | null) => void;
  setQueue: (playerIds: string[]) => void;
  start: () => void;
  pause: () => void;
  resume: () => void;
  undo: () => void;
  /** Auto on or off: my seat, or another as commissioner. */
  setAutopilot: (on: boolean, teamId: string | null) => void;
  /** A league mock's lobby (MOCK_DRAFT.md §6.2): take a seat, moving from any other, or leave mine. */
  takeSeat?: (teamId: string) => void;
  leaveSeat?: () => void;
  /** Trading during the draft (DRAFT_ROOM.md): a room over the socket has it; the practice league has bots. */
  trade?: TradeActions;
  /** The commissioner's: the last trade, taken back. */
  undoTrade?: () => void;
  /** An auction's (AUCTION.md): nominate, bid, and set an auto-bid. */
  auction?: AuctionActions;
}

/**
 * A mock's own controls (MOCK_DRAFT.md §3). Given, the room says *Mock draft*
 * where the real one says *On the clock*, swaps the commissioner's tools for
 * the mock's, leaves out the league's chat, and ends with the grades.
 */
export interface MockControls {
  /** My turns have no clock: the room waits for my pick. */
  untimed: boolean;
  canTakeBack: boolean;
  takeBack: () => void;
  /** The same seat and setup, a fresh draft. */
  startOver: () => void;
  /** Back to the setup. */
  newMock: () => void;
  /** The grades, once every pick is in. */
  grades: ReactNode | null;
  /** What happened to it once it ended: kept with my mocks, on its way, or not kept and why. */
  endNote: string;
}

export interface DraftRoomProps {
  view: DraftView;
  /** The room's clock, in milliseconds. */
  now: number;
  connection: RoomConnection;
  actions: RoomActions;
  /** Where the recap and the team are, once it is over. */
  recapTo: string;
  teamTo: string;
  mock?: MockControls;
  /** My offers that last stopped waiting, to be told (`useLiveDraft`). */
  offerNews?: OfferNews;
  /** The board on a TV (M3, B-608): the league's room and the practice league's; a mock has none. */
  tvTo?: string;
}

export function clock(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  if (s >= 3600) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${String(h)}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  }
  return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, '0')}`;
}

/** One region with a row of tabs over it: the panes a width puts side by side elsewhere. */
function Tabbed({
  label,
  tabs,
  initial,
  className,
}: {
  label: string;
  tabs: { id: string; label: string; count?: number; content: ReactNode }[];
  initial?: string;
  className?: string;
}) {
  const [active, setActive] = useState(initial ?? tabs[0]?.id ?? '');
  const current = tabs.find((t) => t.id === active) ?? tabs[0]!;
  return (
    <section className={`fsp-region fsp-dr-region ${className ?? ''}`} aria-label={label}>
      <div className="fsp-region-head fsp-dr-tabs">
        <Segments label={label} value={current.id} onChange={setActive} items={tabs} />
      </div>
      <div className="fsp-dr-regionbody">{current.content}</div>
    </section>
  );
}

function Region({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`fsp-region fsp-dr-region ${className ?? ''}`} aria-label={title}>
      <div className="fsp-region-head">
        <span className="fsp-cap">{title}</span>
      </div>
      <div className="fsp-dr-regionbody">{children}</div>
    </section>
  );
}

export function DraftRoom({
  view,
  now,
  connection,
  actions,
  recapTo,
  teamTo,
  mock,
  offerNews,
  tvTo,
}: DraftRoomProps) {
  const tier = useTier();
  const navigate = useNavigate();
  const detail = useDetail();
  const zone = useReaderZone() ?? deviceZone();
  const { play } = useDelight();
  const sport = getSport(view.league.sport as never);
  // THE POSITIONS THIS DRAFT DRAFTS, in the sport's own order: the ones
  // somebody on the board or the list plays.
  const positions = useMemo(() => {
    const seen = new Set<string>();
    for (const r of view.available) seen.add(r.player.positions[0] ?? '');
    for (const c of view.board) if (c.player) seen.add(c.player.positions[0] ?? '');
    return sport.positions.map((p) => p.id).filter((p) => seen.has(p));
  }, [view.available, view.board, sport]);

  const seatOf = useMemo(() => new Map(view.seats.map((s) => [s.team.id, s])), [view.seats]);
  const mySeat = view.myTeamId === null ? null : (seatOf.get(view.myTeamId) ?? null);
  const live = view.status === 'live';
  // AN AUCTION'S TURN IS A NOMINATION (AUCTION.md §3.3): its nominator is who
  // the room waits on, as the team on the clock is in a pick draft.
  const auction = view.auction;
  const nominating = auction?.nominating ?? null;
  const isMine = (auction ? Boolean(nominating?.isMine) : Boolean(view.current?.isMine)) && live;
  const onClock = auction
    ? nominating === null
      ? null
      : (seatOf.get(nominating.teamId) ?? null)
    : view.current
      ? (seatOf.get(view.current.teamId) ?? null)
      : null;
  const until = picksUntilMine(view);
  const usable = connection === 'live' || connection === 'practice';
  // A LEAGUE MOCK (MOCK_DRAFT.md §6.2): the league's room, with seats taken in
  // its lobby and run by whoever opened it.
  const together = view.mockRoom;
  const what = together ? 'mock' : 'draft';

  // THE COMMISSIONER PICKING FOR THE TEAM ON THE CLOCK, as Sleeper and ESPN
  // let a commissioner do: a mode they turn on, so every row's button says
  // whose pick it is before it is pressed.
  const [pickForClock, setPickForClock] = useState(false);
  useEffect(() => {
    if (isMine) setPickForClock(false);
  }, [isMine]);
  const pickingFor =
    !usable || !live || (auction ? nominating === null : view.current === null)
      ? null
      : isMine
        ? { teamId: onClock?.team.id ?? '', name: 'you', mine: true }
        : view.isCommissioner && pickForClock && onClock
          ? { teamId: onClock.team.id, name: onClock.team.name, mine: false }
          : null;
  // A PLAYER CHOSEN TO PUT UP, waiting on his opening bid.
  const [nominee, setNominee] = useState<{ playerId: string; forTeam: string | null } | null>(null);
  const choose = (playerId: string, forTeam: string | null) => {
    if (auction) setNominee({ playerId, forTeam });
    else actions.pick(playerId, forTeam);
  };

  // The moments that matter, said: a sound, the tab's title, the phone's tab.
  const wasMine = useRef(false);
  const [phoneTab, setPhoneTab] = useState<string>(view.status === 'lobby' ? 'seats' : 'players');
  useEffect(() => {
    if (isMine && !wasMine.current) {
      play('onTheClock');
      setPhoneTab('players');
    }
    wasMine.current = isMine;
  }, [isMine, play]);
  const ended = mock?.grades != null;
  useEffect(() => {
    if (ended) setPhoneTab('grades');
  }, [ended]);
  // A LEAGUE MOCK ENDS ON ITS BOARD: the players left are nobody's business now.
  const togetherDone = view.mockRoom !== null && view.status === 'complete';
  useEffect(() => {
    if (togetherDone) setPhoneTab('board');
  }, [togetherDone]);
  const lastPick = view.lastPick;
  const wince = useWince(view);
  const lastSeen = useRef<number | null>(lastPick?.overall ?? null);
  useEffect(() => {
    if (lastPick && lastPick.overall !== lastSeen.current && lastPick.teamId === view.myTeamId) {
      play('pick');
    }
    lastSeen.current = lastPick?.overall ?? null;
  }, [lastPick, view.myTeamId, play]);
  useEffect(() => {
    if (!isMine) return;
    const was = document.title;
    document.title = `${auction ? 'Your nomination' : 'Your pick'} · ${was}`;
    return () => {
      document.title = was;
    };
  }, [isMine]);
  // An achievement's moment never covers a pick on the clock (ACHIEVEMENTS.md §12).
  useHoldMoments(isMine || Boolean(auction?.lots.length));

  // ── Trading during the draft (DRAFT_ROOM.md, B-345, B-346) ──────────────
  const trade = actions.trade;
  const canTrade =
    trade !== undefined &&
    mock === undefined &&
    usable &&
    view.trading.allowed &&
    view.status !== 'complete' &&
    view.myTeamId !== null;
  const [tradeSeed, setTradeSeed] = useState<TradeSeed | null>(null);
  /** A seat a person holds, other than mine: one an offer can go to. */
  const canOfferTo = (teamId: string) => {
    if (!canTrade || teamId === view.myTeamId) return false;
    const seat = seatOf.get(teamId);
    return seat !== undefined && seat.ai === null && seat.team.ownerName !== OPEN_SEAT;
  };
  // AN OFFER OF MINE THAT STOPPED WAITING IS SAID, from my side: an answer to
  // mine, one withdrawn from me, and one the board made untrue.
  const toldOffers = useRef(offerNews?.n ?? 0);
  useEffect(() => {
    if (offerNews === undefined || offerNews.n === toldOffers.current) return;
    toldOffers.current = offerNews.n;
    if (view.status === 'complete') return;
    const me = view.myTeamId;
    const name = (teamId: string) => seatOf.get(teamId)?.team.ownerName ?? 'A manager';
    for (const c of offerNews.closed) {
      const mine = c.fromTeamId === me;
      const other = name(mine ? c.toTeamId : c.fromTeamId);
      if (c.reason === 'accepted' && mine) tell(`${other} accepted your offer.`, 'good');
      else if (c.reason === 'declined' && mine) tell(`${other} declined your offer.`);
      else if (c.reason === 'countered' && mine) tell(`${other} countered your offer.`);
      else if (c.reason === 'withdrawn' && !mine) tell(`${other} withdrew their offer.`);
      else if (c.reason === 'void') {
        tell(
          mine
            ? `Your offer to ${other} is off: something in it changed on the board.`
            : `${other}’s offer is off: something in it changed on the board.`,
        );
      }
    }
  }, [offerNews, view.status, view.myTeamId, seatOf]);

  const toggleQueue = (id: string) => {
    const ids = view.myQueue.map((p) => p.id);
    actions.setQueue(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  };

  // ── The clock bar ───────────────────────────────────────────────────────
  const deadline = view.current?.deadlineAt ? Date.parse(view.current.deadlineAt) : null;
  const remaining = deadline === null ? null : deadline - now;
  // THE CLOCK STOPPED OVERNIGHT (C7, B-341): the league's hours, said in the
  // reader's own zone. The deadline already skips them; what a countdown
  // would show meanwhile is the night ticking by, so it says so instead.
  const stoppedUntil =
    live && view.overnightPause !== null
      ? dailyStopEnds(view.overnightPause, view.overnightPause.zone, now)
      : null;
  const overtime =
    remaining !== null &&
    remaining <= 0 &&
    view.whenTheClockExpires === 'off' &&
    live &&
    !mock?.untimed;
  // A MOCK SHOWS A CLOCK ONLY ON MY TURNS, and only when it has one: the bots'
  // turns pass on the mock's own clock, faster than a countdown can be read.
  const showClock = mock === undefined || (isMine && !mock.untimed);
  const startsIn = view.scheduledAt === null ? null : Date.parse(view.scheduledAt) - now;
  const n = view.seats.length;

  const commissionerMenu: MenuEntry[] = [];
  // THE LAST TRADE, TAKEN BACK, beside the last pick: the room refuses one
  // whose picks have been made, and says so.
  const undoTradeEntry: MenuEntry[] =
    view.trading.allowed && actions.undoTrade !== undefined && mock === undefined
      ? [
          {
            kind: 'item',
            key: 'untrade',
            icon: 'undo',
            label: 'Undo the last trade',
            onClick: actions.undoTrade,
            disabled: view.trading.trades.length === 0,
          },
        ]
      : [];
  if (view.isCommissioner && view.status !== 'complete') {
    if (view.status === 'lobby') {
      commissionerMenu.push(
        {
          kind: 'item',
          key: 'start',
          icon: 'play',
          label: together ? 'Start the mock now' : 'Start the draft now',
          onClick: actions.start,
        },
        ...undoTradeEntry,
      );
    } else {
      commissionerMenu.push(
        view.status === 'paused'
          ? {
              kind: 'item',
              key: 'resume',
              icon: 'play',
              label: `Resume the ${what}`,
              onClick: actions.resume,
            }
          : {
              kind: 'item',
              key: 'pause',
              icon: 'pause',
              label: `Pause the ${what}`,
              onClick: actions.pause,
            },
        {
          kind: 'item',
          key: 'undo',
          icon: 'undo',
          label: auction ? 'Undo the last sale' : 'Undo the last pick',
          onClick: actions.undo,
          disabled: view.lastPick === null,
        },
        ...undoTradeEntry,
      );
      if (onClock && !onClock.team.isMine && live) {
        commissionerMenu.push(
          { kind: 'divider' },
          {
            kind: 'item',
            key: 'pickfor',
            icon: 'draft',
            label: pickForClock
              ? `Stop ${auction ? 'nominating' : 'picking'} for ${onClock.team.name}`
              : `${auction ? 'Nominate' : 'Pick'} for ${onClock.team.name}`,
            onClick: () => setPickForClock((v) => !v),
          },
        );
        if (onClock.ai === null) {
          commissionerMenu.push({
            kind: 'item',
            key: 'auto',
            icon: 'bot',
            label: onClock.autopilot
              ? `Take ${onClock.team.name} off auto`
              : `Put ${onClock.team.name} on auto`,
            onClick: () => actions.setAutopilot(!onClock.autopilot, onClock.team.id),
          });
        }
      }
    }
  }

  const mockMenu: MenuEntry[] = [];
  if (mock !== undefined) {
    if (view.status !== 'complete') {
      mockMenu.push(
        view.status === 'paused'
          ? { kind: 'item', key: 'resume', icon: 'play', label: 'Resume', onClick: actions.resume }
          : { kind: 'item', key: 'pause', icon: 'pause', label: 'Pause', onClick: actions.pause },
        {
          kind: 'item',
          key: 'takeback',
          icon: 'undo',
          label: 'Take back my last pick',
          onClick: mock.takeBack,
          disabled: !mock.canTakeBack,
        },
      );
      if (mySeat !== null && !mySeat.autopilot) {
        mockMenu.push({
          kind: 'item',
          key: 'auto',
          icon: 'bot',
          label: 'Auto-draft the rest',
          onClick: () => actions.setAutopilot(true, null),
        });
      }
      mockMenu.push({ kind: 'divider' });
    }
    mockMenu.push(
      {
        kind: 'item',
        key: 'again',
        icon: 'refresh',
        label: 'Start over from this seat',
        onClick: mock.startOver,
      },
      { kind: 'item', key: 'new', icon: 'draft', label: 'New mock', onClick: mock.newMock },
    );
  }
  const menu =
    mock !== undefined ? (
      <Menu
        title="Mock draft"
        align="end"
        entries={[{ kind: 'label', label: 'Mock draft' }, ...mockMenu]}
        target={<IconButton icon="more" variant="quiet" label="Mock draft tools" />}
      />
    ) : commissionerMenu.length > 0 ? (
      <Menu
        title={together ? 'Host' : 'Commissioner'}
        align="end"
        entries={[
          { kind: 'label', label: together ? 'Host' : 'Commissioner' },
          ...commissionerMenu,
        ]}
        target={
          <IconButton
            icon="more"
            variant="quiet"
            label={together ? 'Host tools' : 'Commissioner tools'}
          />
        }
      />
    ) : null;

  const bar = (
    <div className="fsp-clockbar fsp-dr-bar" data-mine={isMine || undefined}>
      {view.status === 'lobby' && together ? (
        <div className="fsp-ui-grow">
          <Caption>League mock</Caption>
          <div className="fsp-draftroom-headline">
            {together.isHost ? 'You are hosting' : `Hosted by ${together.host}`}
          </div>
          <div className="fsp-sub">
            {view.seats.filter((s) => s.team.ownerName !== OPEN_SEAT).length} of {n} seats taken ·{' '}
            {view.pickSeconds} sec a pick
          </div>
        </div>
      ) : view.status === 'lobby' ? (
        <>
          <div className="fsp-ui-grow">
            <Caption>Draft lobby</Caption>
            <div className="fsp-draftroom-headline">
              {view.scheduledAt === null
                ? 'No start time yet'
                : `Starts ${formatInZone(view.scheduledAt, zone, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}`}
            </div>
            <div className="fsp-sub">
              {view.seats.filter((s) => s.connected && s.ai === null).length} of{' '}
              {view.seats.filter((s) => s.team.ownerName !== OPEN_SEAT || s.ai !== null).length}{' '}
              here · {auction ? `an auction, $${String(auction.rules.budget)} a team · ` : ''}open
              seats get a bot when it starts
            </div>
          </div>
          {startsIn !== null && startsIn > 0 ? (
            <span
              className="fsp-num fsp-disp fsp-clock-digits"
              aria-label={`Starts in ${clock(startsIn)}`}
            >
              {clock(startsIn)}
            </span>
          ) : null}
        </>
      ) : auction && view.status !== 'complete' ? (
        (() => {
          const t = auctionBarText(view, now);
          return (
            <>
              {onClock && auction.lots.length === 0 ? (
                <TeamMark
                  team={onClock.team}
                  size={tier === 'phone' ? 28 : 36}
                  face={wince === onClock.team.id ? 'annoyed' : 'thinking'}
                />
              ) : null}
              <div className="fsp-ui-grow fsp-dr-bartext">
                <Caption>
                  {mock || together ? `Mock ${t.caption.toLowerCase()}` : t.caption}
                </Caption>
                <div className="fsp-draftroom-headline fsp-ui-nowrap">{t.headline}</div>
                <div className="fsp-sub">{t.sub}</div>
              </div>
              {t.clock !== null ? (
                <span
                  className="fsp-num fsp-disp fsp-clock-digits"
                  data-urgent={t.urgent || undefined}
                  aria-live="off"
                >
                  {stoppedUntil !== null ? '—' : t.clock}
                </span>
              ) : null}
            </>
          );
        })()
      ) : view.status === 'complete' ? (
        <div className="fsp-ui-grow">
          <Caption>{mock || together ? 'Mock complete' : 'Draft complete'}</Caption>
          <div className="fsp-draftroom-headline">
            {auction
              ? 'Every roster is full.'
              : mock || together
                ? 'Every pick is in.'
                : 'Every pick is in. Rosters are set.'}
          </div>
        </div>
      ) : (
        <>
          {onClock ? (
            <TeamMark
              team={onClock.team}
              size={tier === 'phone' ? 28 : 36}
              face={wince === onClock.team.id ? 'annoyed' : 'thinking'}
            />
          ) : null}
          <div className="fsp-ui-grow fsp-dr-bartext">
            <Caption>
              {mock || together
                ? view.status === 'paused'
                  ? 'Mock draft · paused'
                  : isMine
                    ? 'Mock draft · your pick'
                    : 'Mock draft'
                : view.status === 'paused'
                  ? 'Paused'
                  : isMine
                    ? 'Your pick'
                    : 'On the clock'}
            </Caption>
            <div className="fsp-draftroom-headline fsp-ui-nowrap">
              {isMine ? 'You' : (onClock?.team.name ?? '')}
              {view.current
                ? ` · ${pickLabel(view.current.round, ((view.current.overall - 1) % n) + 1)}`
                : ''}
            </div>
            <div className="fsp-sub">
              {until !== null && until > 0 ? `Your pick in ${String(until)} · ` : ''}
              {lastPick?.player
                ? `Last: ${seatOf.get(lastPick.teamId)?.team.isMine ? 'you' : (seatOf.get(lastPick.teamId)?.team.ownerName ?? '')} took ${lastPick.player.shortName}`
                : 'First pick'}
            </div>
          </div>
          {showClock ? (
            <span
              className="fsp-num fsp-disp fsp-clock-digits"
              data-urgent={
                remaining !== null && remaining <= 10_000 && remaining > 0 && live
                  ? 'true'
                  : undefined
              }
              aria-live="off"
            >
              {view.status === 'paused' || stoppedUntil !== null
                ? '—'
                : remaining === null
                  ? '—'
                  : clock(remaining)}
            </span>
          ) : null}
        </>
      )}
      <div className="fsp-dr-bartools">
        {/* THE BOARD ON A TV (M3): the icon alone on a phone, whose screen
            may be the one cast. */}
        {tvTo !== undefined ? (
          tier === 'phone' ? (
            <IconButton
              icon="tv"
              variant="quiet"
              label="Show the draft on a TV"
              onClick={() => navigate(tvTo)}
            />
          ) : (
            <Button size="xs" variant="quiet" icon="tv" to={tvTo} label="Show the draft on a TV">
              On a TV
            </Button>
          )
        ) : null}
        {mySeat !== null && view.status !== 'complete' ? (
          <Toggle
            label="Auto"
            on={mySeat.autopilot}
            disabled={!usable}
            onChange={(on) => actions.setAutopilot(on, null)}
          />
        ) : null}
        {menu}
      </div>
    </div>
  );

  // ── What the room needs said ────────────────────────────────────────────
  const notices: ReactNode[] = [];
  if (connection === 'reconnecting' || connection === 'connecting') {
    notices.push(
      <Callout key="conn" tone="warn">
        {connection === 'connecting'
          ? 'Joining the room…'
          : 'Reconnecting to the room… The board stays as it was until it is back.'}
      </Callout>,
    );
  } else if (connection === 'offline') {
    notices.push(
      <Callout key="conn" tone="bad">
        You are offline. The room keeps drafting; this screen catches up when it is back.
      </Callout>,
    );
  } else if (connection === 'signed-out') {
    notices.push(
      <Callout key="conn" tone="bad">
        Your session ended. Sign in again to come back to the room.
      </Callout>,
    );
  } else if (connection === 'removed') {
    notices.push(
      <Callout key="conn" tone="bad">
        You are no longer in this league, so the room has closed for you.
      </Callout>,
    );
  }
  // AN OFFER TO ME, under the clock until I answer it, whichever tab is open.
  if (trade !== undefined && view.status !== 'complete') {
    for (const offer of view.trading.offers.filter((o) => o.toTeamId === view.myTeamId)) {
      notices.push(
        <OfferToMe
          key={`offer-${offer.id}`}
          view={view}
          offer={offer}
          actions={trade}
          usable={usable}
          onCounter={setTradeSeed}
        />,
      );
    }
  }
  if (mySeat?.autopilot && view.status !== 'complete') {
    notices.push(
      <Callout key="auto" tone="accent">
        <span className="fsp-dr-notice">
          <span>
            {auction
              ? 'Auto is on: the room nominates for you, and bids up to your prices, else the values.'
              : 'Auto is on: the room picks for you a moment into each of your turns.'}
          </span>
          <Button size="xs" variant="secondary" onClick={() => actions.setAutopilot(false, null)}>
            Turn auto off
          </Button>
        </span>
      </Callout>,
    );
  }
  if (view.status === 'paused') {
    notices.push(
      <Callout key="paused" tone="warn">
        {mock
          ? 'Paused. Nothing moves until you resume.'
          : together
            ? together.isHost
              ? `Paused. The clock starts again when you resume, or by itself after ${String(LEAGUE_MOCK_PAUSE_MINUTES)} minutes.`
              : `${together.host} paused the mock. The clock starts again when it resumes, or by itself after ${String(LEAGUE_MOCK_PAUSE_MINUTES)} minutes.`
            : 'The commissioner paused the draft. The clock starts again when it resumes.'}
      </Callout>,
    );
  }
  if (stoppedUntil !== null) {
    notices.push(
      <Callout key="night" tone="neutral">
        The clock is stopped overnight until{' '}
        {formatInZone(stoppedUntil, zone, { hour: 'numeric', minute: '2-digit' })}.{' '}
        {auction ? 'A bid made now still counts.' : 'A pick made now still counts.'}
      </Callout>,
    );
  }
  if (overtime) {
    notices.push(
      <Callout key="over" tone="neutral">
        {isMine
          ? 'Your clock is at zero. This league waits for you, so take your time.'
          : `${onClock?.team.name ?? 'This team'}’s clock is at zero. This league waits for every pick.`}
      </Callout>,
    );
  }
  // AN AUCTION'S NOMINATION AT ZERO under the soft timer: the room waits.
  const nominateLeft = nominating?.deadlineAt ? Date.parse(nominating.deadlineAt) - now : null;
  if (
    auction &&
    live &&
    nominateLeft !== null &&
    nominateLeft <= 0 &&
    view.whenTheClockExpires === 'off' &&
    auction.lots.length === 0
  ) {
    notices.push(
      <Callout key="nomover" tone="neutral">
        {isMine
          ? 'Your nomination clock is at zero. This league waits for you.'
          : `${onClock?.team.name ?? 'This team'}’s nomination clock is at zero. This league waits.`}
      </Callout>,
    );
  }
  if (pickingFor !== null && !pickingFor.mine) {
    notices.push(
      <Callout key="pickfor" tone="accent">
        {auction
          ? `You are nominating for ${pickingFor.name}. Choose a player and an opening bid.`
          : `You are picking for ${pickingFor.name}. Pick a player, then Confirm.`}
      </Callout>,
    );
  }
  // SAID BEFORE THE START, in a league mock's lobby: what the start does to
  // the seats nobody took, and that a lobby nobody starts closes.
  if (view.status === 'lobby' && together) {
    const closes =
      together.closesAt === null
        ? ''
        : ` If nobody starts it, it closes at ${formatInZone(together.closesAt, zone, { hour: 'numeric', minute: '2-digit' })}.`;
    notices.push(
      <Callout key="together" tone="neutral">
        {mySeat === null
          ? `Take any open seat. When ${together.isHost ? 'you start' : `${together.host} starts`}, a bot takes every seat nobody took.${closes}`
          : together.isHost
            ? `Start when everybody is in. A bot takes every seat nobody took.${closes}`
            : `You are in. ${together.host} starts it, and a bot takes every seat nobody took.${closes}`}
      </Callout>,
    );
  }
  if (view.status === 'complete' && together) {
    notices.push(
      <Callout key="done" tone="good">
        <span className="fsp-dr-notice">
          <span>
            {mySeat !== null
              ? 'The mock is done. Your seat is kept in your mocks, graded.'
              : 'The mock is done. Each seat is kept in its drafter’s mocks.'}
          </span>
          <span className="fsp-dr-noticebtns">
            <Button size="xs" variant="primary" to={`/l/${view.league.id}/draft/mock`}>
              Your mocks
            </Button>
          </span>
        </span>
      </Callout>,
    );
  } else if (view.status === 'complete' && mock) {
    notices.push(
      <Callout key="done" tone="good">
        <span className="fsp-dr-notice">
          <span>{mock.endNote}</span>
          <span className="fsp-dr-noticebtns">
            <Button size="xs" variant="primary" onClick={mock.newMock}>
              Run another
            </Button>
            <Button size="xs" onClick={mock.startOver}>
              Same seat again
            </Button>
          </span>
        </span>
      </Callout>,
    );
  } else if (view.status === 'complete') {
    notices.push(
      <Callout key="done" tone="good">
        <span className="fsp-dr-notice">
          <span>
            {auction
              ? 'The auction is done. Every roster is full.'
              : 'The draft is done. Every roster is set.'}
          </span>
          <span className="fsp-dr-noticebtns">
            <Button size="xs" variant="primary" to={recapTo}>
              Draft recap
            </Button>
            <Button size="xs" to={teamTo}>
              My team
            </Button>
          </span>
        </span>
      </Callout>,
    );
  }

  // ── The panes ───────────────────────────────────────────────────────────
  const players = (
    <PlayersPane
      view={view}
      positions={positions}
      detail={detail}
      tier={tier}
      pickingFor={pickingFor}
      onPick={(id) => choose(id, pickingFor && !pickingFor.mine ? pickingFor.teamId : null)}
      onToggleQueue={toggleQueue}
      nominate={auction !== null}
    />
  );
  const board = <BoardPane view={view} auction={auction !== null} />;
  // EVERY TEAM'S MONEY FIRST, in an auction: who can still pay is what one needs most.
  const money = auction ? <MoneyPane view={view} /> : null;
  const teamsOnly = (
    <>
      <TeamsPane view={view} positions={positions} />
      <TradesMade view={view} />
    </>
  );
  const teams = (
    <>
      {money}
      {teamsOnly}
    </>
  );
  const tiers = <TiersPane view={view} positions={positions} />;
  const picks = <PicksPane view={view} />;
  const queue = <QueuePane view={view} onSetQueue={actions.setQueue} mock={mock !== undefined} />;
  const team = (
    <>
      {trade !== undefined ? <MyOffers view={view} actions={trade} usable={usable} /> : null}
      <MyTeamPane view={view} />
    </>
  );
  const seats = (
    <SeatsPane
      view={view}
      seatActions={
        together && view.status === 'lobby' && usable && actions.takeSeat && actions.leaveSeat
          ? { take: actions.takeSeat, leave: actions.leaveSeat }
          : null
      }
    />
  );
  // A MOCK IS NOT THE LEAGUE'S, so the league's chat stays out of it.
  const chat = mock ? null : <ChatPanel compact />;
  const grades = mock?.grades ?? null;
  /** A row of tabs without the chat a mock leaves out, and with the grades first once it ends. */
  const tabsOf = (tabs: { id: string; label: string; count?: number; content: ReactNode }[]) => [
    ...(grades !== null ? [{ id: 'grades', label: 'Grades', content: grades }] : []),
    ...tabs.filter((t) => t.id !== 'chat' || chat !== null),
  ];
  const queued = view.myQueue.length;
  const lobby = view.status === 'lobby';

  // EVERY NAME IN THE ROOM OPENS THE DRAFT'S OWN CARD, and every team's its
  // draft so far (B-337, B-338), with the room's moves on the card.
  const cardActions: DraftCardActions | null =
    view.status === 'complete' || !usable
      ? null
      : {
          pickingFor,
          pick: choose,
          verb: auction ? 'Nominate' : 'Draft',
          queued: (id) => view.myQueue.some((p) => p.id === id),
          toggleQueue: view.myTeamId === null ? null : toggleQueue,
        };
  const withCards = (node: ReactNode) => (
    <DraftCards
      seats={view.seats}
      board={view.board}
      available={view.available}
      actions={cardActions}
      trading={{
        canOfferTo,
        propose: (teamId) =>
          setTradeSeed({ partnerTeamId: teamId, give: [], get: [], replaces: null }),
        on: view.trading.allowed || view.trading.trades.length > 0,
      }}
    >
      {node}
      {trade !== undefined ? (
        <TradeSheet
          view={view}
          seed={canTrade ? tradeSeed : null}
          actions={trade}
          onClose={() => setTradeSeed(null)}
        />
      ) : null}
    </DraftCards>
  );

  const head = (
    <div className="fsp-pinned fsp-dr-pinned">
      {bar}
      {auction && actions.auction && (live || view.status === 'paused') ? (
        <AuctionLots
          view={view}
          now={now}
          actions={actions.auction}
          usable={usable}
          stopped={stoppedUntil !== null}
          detail={detail}
          nominate={nominee}
          onNominated={() => setNominee(null)}
        />
      ) : null}
      {notices.length > 0 ? <div className="fsp-dr-notices">{notices}</div> : null}
    </div>
  );

  if (tier === 'phone') {
    const tabs = lobby
      ? tabsOf([
          { id: 'seats', label: 'Seats', content: seats },
          { id: 'players', label: 'Players', content: players },
          { id: 'mine', label: 'Queue', count: queued, content: queue },
          { id: 'chat', label: 'Chat', content: chat },
        ])
      : tabsOf([
          { id: 'players', label: 'Players', content: players },
          { id: 'board', label: 'Board', content: board },
          {
            id: 'teams',
            label: 'Teams',
            content: (
              <>
                {teams}
                {tiers}
              </>
            ),
          },
          {
            id: 'mine',
            label: 'Mine',
            count: queued,
            content: (
              <>
                {queue}
                {team}
              </>
            ),
          },
          { id: 'chat', label: 'Chat', content: chat },
        ]);
    const current = tabs.find((t) => t.id === phoneTab) ?? tabs[0]!;
    return withCards(
      <div className="fsp-screen fsp-dr" data-tier="phone">
        {head}
        <div className="fsp-pinned">
          <Segments label="Draft room" value={current.id} onChange={setPhoneTab} items={tabs} />
        </div>
        <div className="fsp-regions fsp-board-one">
          <section className="fsp-region fsp-region-plain fsp-dr-region" aria-label={current.label}>
            {current.content}
          </section>
        </div>
      </div>,
    );
  }

  if (lobby) {
    return withCards(
      <div className="fsp-screen fsp-dr" data-tier={tier} data-status="lobby">
        {head}
        <div className="fsp-regions fsp-dr-grid fsp-dr-lobby">
          <Region title="Seats" className="fsp-dr-a-seats">
            {seats}
          </Region>
          <Region title="Players" className="fsp-dr-a-players">
            {players}
          </Region>
          <Tabbed
            label="Mine"
            className="fsp-dr-a-lobbymine"
            tabs={tabsOf([
              { id: 'queue', label: 'Queue', count: queued, content: queue },
              { id: 'chat', label: 'Chat', content: chat },
            ])}
          />
        </div>
      </div>,
    );
  }

  return withCards(
    <div className="fsp-screen fsp-dr" data-tier={tier} data-status={view.status}>
      {head}
      <div className="fsp-regions fsp-dr-grid">
        <Region title="Board" className="fsp-dr-a-board">
          {board}
        </Region>
        <Region title={lobby ? 'Players' : 'Players left'} className="fsp-dr-a-players">
          {players}
        </Region>
        {tier === 'tablet' ? (
          <Tabbed
            key={grades !== null ? 'done' : 'live'}
            label="More"
            className="fsp-dr-a-more"
            tabs={tabsOf([
              ...(auction ? [{ id: 'teams', label: 'Teams', content: teams }] : []),
              { id: 'queue', label: 'Queue', count: queued, content: queue },
              { id: 'team', label: 'Team', content: team },
              ...(auction ? [] : [{ id: 'teams', label: 'Teams', content: teams }]),
              { id: 'tiers', label: 'Tiers', content: tiers },
              { id: 'chat', label: 'Chat', content: chat },
            ])}
          />
        ) : tier === 'desktop' ? (
          <>
            <Tabbed
              key={grades !== null ? 'done' : 'live'}
              label="Research"
              className="fsp-dr-a-research"
              tabs={tabsOf(
                auction
                  ? [
                      { id: 'teams', label: 'Teams', content: teams },
                      { id: 'tiers', label: 'Tiers', content: tiers },
                    ]
                  : [
                      { id: 'tiers', label: 'Tiers', content: tiers },
                      { id: 'teams', label: 'Teams', content: teams },
                      { id: 'picks', label: 'Picks', content: picks },
                    ],
              )}
            />
            <Tabbed
              label="Mine"
              className="fsp-dr-a-mine"
              tabs={[
                { id: 'queue', label: 'Queue', count: queued, content: queue },
                { id: 'team', label: 'Team', content: team },
                ...(chat !== null ? [{ id: 'chat', label: 'Chat', content: chat }] : []),
              ]}
            />
          </>
        ) : (
          <>
            <Region
              title={grades !== null ? 'Grades' : auction ? 'Money' : 'Tiers'}
              className="fsp-dr-a-tiers"
            >
              {grades ?? money ?? tiers}
            </Region>
            <Region title="Teams" className="fsp-dr-a-teams">
              {/* The money has its own region at this width. */}
              {teamsOnly}
            </Region>
            <Tabbed
              label="Mine"
              className="fsp-dr-a-mine"
              tabs={[
                { id: 'queue', label: 'Queue', count: queued, content: queue },
                { id: 'team', label: 'Team', content: team },
                auction
                  ? { id: 'tiers', label: 'Tiers', content: tiers }
                  : { id: 'picks', label: 'Picks', content: picks },
                ...(chat !== null ? [{ id: 'chat', label: 'Chat', content: chat }] : []),
              ]}
            />
          </>
        )}
      </div>
    </div>,
  );
}
